/* SAMSON MWITA: solid at the top, clear while scrolling down, solid on return.
   Native menu/filter drawers continue to own their links, focus and forms. */
(() => {
  if (window.smEditorialHeader) {
    window.smEditorialHeader.refresh();
    return;
  }
  let header;
  let main;
  let collection;
  let footer;
  let lastScrollY = 0;
  let scrollDirection = 'down';
  let directionDistance = 0;
  let frame = 0;
  let refreshFrame = 0;
  let sizeObserver;
  let stateObserver;
  let group;
  let menu;
  let drawer;
  let menuButton;
  let filterButton;
  let filters;
  let spacer;
  let expandedHeight = 0;
  let compactHeight = 0;
  let measuredWidth = 0;
  let measuredViewportWidth = 0;
  let mobileGroupHeight = 0;
  let groupExtraHeight = 0;
  let observedHeaderHeight = 0;
  let observedGroupHeight = 0;
  let scrollPending = false;
  let wasMenuOpen = false;
  let navigation;
  let anchors = [];
  let inkTargets = [];
  let progress = 0;
  let target = 0;
  let lastTime = null;
  let refreshing = false;
  let needsMeasurement = false;
  const motionStyles = new Map();
  const duration = 320;
  const menuSwitch = 70 / duration;
  const inkSelector = '.logo a, .tools button, .mobileTrigger button, .sm-header-filter, .menuPrimary > ul > li > a, .menuPrimary > ul > li > details > summary';
  const photoSelector = '.sm-split__link, .sm-editorial-panels__link, .sm-image-feature__link';
  const desktop = window.matchMedia('(min-width: 778px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = value => Math.min(1, Math.max(0, value));
  const ease = value => { const p = clamp(value); return p * p * (3 - 2 * p); };
  const px = value => `${Math.round(value * 1000) / 1000}px`;

  function motionAllowed() {
    return desktop.matches && !reducedMotion.matches &&
      ['mega', 'drop'].includes(header.dataset.smOriginalMenu) &&
      header.dataset.smSiteMotion !== 'false' && !window.Shopify?.designMode;
  }

  function writeMotionStyle(element, property, value) {
    if (!element) return;
    if (!motionStyles.has(element)) motionStyles.set(element, new Map());
    const properties = motionStyles.get(element);
    if (!properties.has(property)) properties.set(property, {
      value: element.style.getPropertyValue(property),
      priority: element.style.getPropertyPriority(property),
    });
    if (element.style.getPropertyValue(property) !== value) element.style.setProperty(property, value);
  }

  function restoreProperty(element, property) {
    const original = motionStyles.get(element)?.get(property);
    if (!original) return;
    if (original.value) element.style.setProperty(property, original.value, original.priority);
    else element.style.removeProperty(property);
  }

  function clearMotionStyles() {
    motionStyles.forEach((properties, element) => properties.forEach((_, property) => restoreProperty(element, property)));
    motionStyles.clear();
  }

  function collapsed() {
    return header.dataset.smMenuCollapsed === 'true';
  }

  function setMenuMode(compact) {
    header.dataset.smMenuCollapsed = String(compact);
    header.setAttribute('coretex-menu', compact ? 'drawer' : header.dataset.smOriginalMenu);
    if (filterButton) filterButton.hidden = !(compact && filters?.isConnected);
  }

  function measureExpandedHeader() {
    if (expandedHeight && navigationBusy()) {
      needsMeasurement = true;
      return;
    }
    needsMeasurement = false;
    measuredViewportWidth = window.innerWidth;
    if (!desktop.matches) {
      // Mobile has one navigation layout. Measuring the desktop expansion and
      // collapse here needlessly invalidates layout while browser chrome moves.
      clearMotionStyles();
      if (header.dataset.smMenuCollapsed !== 'false') setMenuMode(false);
      const bounds = header.getBoundingClientRect();
      expandedHeight = compactHeight = observedHeaderHeight = bounds.height;
      measuredWidth = bounds.width;
      mobileGroupHeight = observedGroupHeight = group.getBoundingClientRect().height;
      groupExtraHeight = mobileGroupHeight - expandedHeight;
      return;
    }
    // Read natural geometry, without a transition's temporary height/offsets.
    // Both native modes are measured synchronously; only the restored mode paints.
    const compact = collapsed();
    clearMotionStyles();
    setMenuMode(false);
    let bounds = header.getBoundingClientRect();
    expandedHeight = bounds.height;
    measuredWidth = bounds.width;
    groupExtraHeight = group.getBoundingClientRect().height - expandedHeight;
    anchors.forEach(anchor => {
      const box = anchor.element.getBoundingClientRect();
      anchor.expanded = {x: box.left - bounds.left, y: box.top - bounds.top};
    });
    setMenuMode(true);
    bounds = header.getBoundingClientRect();
    compactHeight = bounds.height;
    anchors.forEach(anchor => {
      const box = anchor.element.getBoundingClientRect();
      anchor.compact = {x: box.left - bounds.left, y: box.top - bounds.top};
    });
    setMenuMode(compact);
    observedHeaderHeight = compact ? compactHeight : expandedHeight;
    observedGroupHeight = observedHeaderHeight + groupExtraHeight;
  }

  function renderNavigation(busy) {
    // Fade inline links first, then change the native mode at zero opacity.
    // A single reversible progress value keeps rapid scroll changes continuous.
    const compact = progress > menuSwitch;
    if (compact !== collapsed()) {
      if (compact && !drawer.hasAttribute('open') && !drawer.hasAttribute('closing')) drawer.setAttribute('closed', '');
      setMenuMode(compact);
    }
    if (progress === 0 || progress === 1) {
      clearMotionStyles();
      return compact ? compactHeight : expandedHeight;
    }
    const layoutProgress = clamp((progress - menuSwitch) / (1 - menuSwitch));
    const blend = ease(layoutProgress);
    const height = expandedHeight + (compactHeight - expandedHeight) * blend;
    writeMotionStyle(header, 'height', px(height));
    anchors.forEach(anchor => {
      if (!anchor.expanded || !anchor.compact) return;
      const base = compact ? anchor.compact : anchor.expanded;
      const x = anchor.expanded.x + (anchor.compact.x - anchor.expanded.x) * blend - base.x;
      const y = anchor.expanded.y + (anchor.compact.y - anchor.expanded.y) * blend - base.y;
      writeMotionStyle(anchor.element, 'translate', `${px(x)} ${px(y)}`);
    });
    if (compact) {
      // Never fade or transform the drawer or an ancestor of its fixed panel.
      restoreProperty(navigation, 'opacity');
      const opacity = busy ? 1 : ease(layoutProgress * 1.8);
      [menuButton, filterButton].forEach(element => writeMotionStyle(element, 'opacity', String(opacity)));
    } else {
      [menuButton, filterButton].forEach(element => restoreProperty(element, 'opacity'));
      writeMotionStyle(navigation, 'opacity', String(busy ? 1 : 1 - ease(progress / menuSwitch)));
    }
    return height;
  }

  function navigationBusy() {
    return drawer?.hasAttribute('open') || drawer?.hasAttribute('closing') ||
      (desktop.matches && menu?.contains(document.activeElement)) || Boolean(menu?.querySelector('details.lv1[open]'));
  }

  function updateNavigation() {
    if (!menu || !drawer || !group) return;
    if (needsMeasurement || (!sizeObserver && (Math.abs(header.getBoundingClientRect().width - measuredWidth) > 0.5 ||
        (desktop.matches && progress === 0 && Math.abs(header.getBoundingClientRect().height - expandedHeight) > 0.5)))) {
      measureExpandedHeader();
    }
    const pinned = header.getAttribute('location') === 'top' &&
      ['sticky', 'fixed'].includes(header.getAttribute('mode'));
    const open = drawer.hasAttribute('open');
    const busy = navigationBusy();
    const y = Math.max(0, window.scrollY || 0);
    let compact = desktop.matches && pinned && (y > 144 || (target === 1 && y > 40));
    // Never turn an active dialog into inline navigation, or hide a focused link.
    if (desktop.matches && pinned && busy) compact = collapsed() || open;
    target = compact ? 1 : 0;
    if (refreshing || !motionAllowed()) {
      progress = target;
      lastTime = null;
    } else if (progress !== target) {
      const now = window.performance.now();
      const step = lastTime === null ? 0 : Math.max(0, now - lastTime) / duration;
      progress = target > progress ? Math.min(target, progress + step) : Math.max(target, progress - step);
      lastTime = progress === target ? null : now;
    } else {
      lastTime = null;
    }
    // The animation already knows its border-box height. Re-reading the header
    // after writing it forced a synchronous layout on every animation frame.
    const height = renderNavigation(busy);
    const reserve = header.getAttribute('mode') === 'sticky'
      ? Math.max(0, expandedHeight - height) : 0;
    if (spacer && spacer.style.height !== px(reserve)) spacer.style.height = px(reserve);
    if (pinned) {
      const groupHeight = desktop.matches ? height + groupExtraHeight : mobileGroupHeight;
      // Only sticky offsets follow the compact bar. Media height constraints
      // keep the expanded value, so garments do not resize during a scroll.
      const metrics = {
        '--headSpace': px(groupHeight),
        '--headerHeight': px(height),
        '--headerGroupHeight': px(groupHeight + Math.max(0, expandedHeight - height)),
      };
      Object.entries(metrics).forEach(([property, value]) => {
        if (document.body.style.getPropertyValue(property) !== value) document.body.style.setProperty(property, value);
      });
    } else {
      document.body.style.removeProperty('--headSpace');
    }
    if (filterButton) {
      const hidden = !(collapsed() && filters?.isConnected);
      if (filterButton.hidden !== hidden) filterButton.hidden = hidden;
      const expanded = String(Boolean(filters?.hasAttribute('open')));
      if (filterButton.getAttribute('aria-expanded') !== expanded) filterButton.setAttribute('aria-expanded', expanded);
    }
    if (menuButton && menuButton.getAttribute('aria-expanded') !== String(open)) menuButton.setAttribute('aria-expanded', String(open));
    if (wasMenuOpen && !open && (document.activeElement === document.body || drawer.contains(document.activeElement))) {
      menuButton?.focus({preventScroll: true});
    }
    wasMenuOpen = open;
    if (progress !== target) queueUpdate();
  }

  function scrollPosition() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const pageHeight = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0);
    const maximum = Math.max(0, pageHeight - viewportHeight);
    // Clamp iOS rubber-band overscroll so bouncing cannot reverse the header.
    return {y: Math.max(0, Math.min(window.scrollY || 0, maximum)), maximum, viewportHeight};
  }

  function trackScroll(y) {
    const delta = y - lastScrollY;
    if (delta) {
      directionDistance = Math.sign(delta) === Math.sign(directionDistance) ? directionDistance + delta : delta;
      // Small touch/trackpad movements do not flicker between the two surfaces.
      if (Math.abs(directionDistance) >= 8) scrollDirection = delta > 0 ? 'down' : 'up';
    }
    lastScrollY = y;
  }

  function onScroll() {
    // Leave document height/layout reads to the single scheduled frame.
    scrollPending = true;
    queueUpdate();
  }

  function setSurface(value) {
    if (header.dataset.smHeaderSurface !== value) header.dataset.smHeaderSurface = value;
  }

  function mediaCoversPoint(media, x, y, style, box) {
    if (!box.width || !box.height || style.visibility === 'hidden' || Number(style.opacity || 1) === 0) return false;
    if (x < box.left || x > box.right || y < box.top || y > box.bottom) return false;
    const width = media.naturalWidth || media.videoWidth;
    const height = media.naturalHeight || media.videoHeight;
    if (media.tagName === 'IMG' && (!media.complete || !width)) return false;
    if (media.tagName === 'VIDEO' && !width && !media.poster) return false;
    // A contained photograph can leave a pale band inside its element's box.
    if (style.objectFit !== 'contain' || !width || !height) return true;
    const scale = Math.min(box.width / width, box.height / height);
    const [horizontal = '50%', vertical = '50%'] = style.objectPosition.split(/\s+/);
    const offset = (position, space) => {
      const keywords = {left: 0, top: 0, center: 0.5, right: 1, bottom: 1};
      if (position in keywords) return space * keywords[position];
      return position.endsWith('%') ? space * parseFloat(position) / 100 : parseFloat(position) || 0;
    };
    const left = box.left + offset(horizontal, box.width - width * scale);
    const top = box.top + offset(vertical, box.height - height * scale);
    return x >= left && x <= left + width * scale && y >= top && y <= top + height * scale;
  }

  function updateContrast() {
    if (!document.elementsFromPoint || !main) return 'dark';
    // Cache reads for this frame only: responsive images and section settings
    // can change, and each control may sit over a different panel or margin.
    const styles = new Map();
    const photos = new Map();
    const bounds = new Map();
    const boundsOf = element => {
      if (!bounds.has(element)) bounds.set(element, element.getBoundingClientRect());
      return bounds.get(element);
    };
    const styleOf = element => {
      if (!styles.has(element)) styles.set(element, window.getComputedStyle(element));
      return styles.get(element);
    };
    const inkForColor = rgb => (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) >= 166 ? '#111' : '#fff';
    const inkAt = (x, y) => {
      let alpha = 0;
      const rgb = [0, 0, 0];
      for (const element of document.elementsFromPoint(x, y)) {
        // Skip the header itself without changing pointer events or hit areas.
        if (!main.contains(element) && element !== document.body && element !== document.documentElement) continue;
        const style = styleOf(element);
        if (style.visibility === 'hidden' || Number(style.opacity || 1) === 0) continue;
        const photo = element.closest(photoSelector);
        if (photo) {
          if (!photos.has(photo)) photos.set(photo, [...photo.querySelectorAll('img, video, svg[class*="placeholder"]')]);
          if (photos.get(photo).some(media => mediaCoversPoint(media, x, y, styleOf(media), boundsOf(media)))) {
            // Editorial panels already carry the chosen contrast for their
            // photography/video. White product cards use their CSS background.
            const panel = photo.closest('.sm-split__panel, .sm-editorial-panels__panel, .sm-image-feature');
            return panel?.matches('.sm-split__panel--dark, .sm-editorial-panels__panel--dark, .sm-image-feature--dark') ? '#111' : '#fff';
          }
        }
        const color = style.backgroundColor.match(/^rgba?\(([^)]+)\)$/);
        if (!color) continue;
        const components = color[1].split(/[\s,/]+/).map(Number);
        const layerAlpha = (components[3] ?? 1) * (1 - alpha);
        for (let index = 0; index < 3; index++) rgb[index] += components[index] * layerAlpha;
        alpha += layerAlpha;
        if (alpha >= 0.99) return inkForColor(rgb);
      }
      return inkForColor(rgb.map(value => value + 255 * (1 - alpha)));
    };
    let wordmarkInk = '#111';
    const inkUpdates = [];
    inkTargets.forEach(element => {
      // Mobile uses the drawer even when the desktop setting is still "mega".
      // Its hidden links retain geometry but never need backdrop sampling.
      if ((!desktop.matches || header.getAttribute('coretex-menu') === 'drawer') && drawer?.contains(element)) return;
      const box = element.getBoundingClientRect();
      if (!box.width || !box.height || box.bottom <= 0 || box.top >= window.innerHeight) return;
      const y = Math.max(0, Math.min(window.innerHeight - 1, box.top + box.height / 2));
      // A compact phone icon only needs its centre sampled. Keep three points
      // for the wider wordmark and desktop links that can cross two panels.
      const samples = !desktop.matches && box.width <= 64 ? [0.5] : [0.25, 0.5, 0.75];
      const votes = samples.map(fraction => inkAt(
        Math.max(0, Math.min(window.innerWidth - 1, box.left + box.width * fraction)), y
      ));
      const ink = votes.filter(value => value === '#fff').length > votes.length / 2 ? '#fff' : '#111';
      inkUpdates.push([element, ink]);
      if (element.closest('.logo')) wordmarkInk = ink;
    });
    // Finish all layout reads before writing colours to avoid repeated reflow.
    inkUpdates.forEach(([element, ink]) => {
      if (element.style.getPropertyValue('--sm-header-local-ink') !== ink) element.style.setProperty('--sm-header-local-ink', ink);
    });
    return wordmarkInk === '#fff' ? 'light' : 'dark';
  }

  function update() {
    frame = 0;
    if (!header?.isConnected) return;
    const position = scrollPosition();
    if (scrollPending) {
      scrollPending = false;
      trackScroll(position.y);
    }
    updateNavigation();
    if (header.dataset.smTransparentHeader !== 'true') return;
    // The Fleur frame owns a solid surface throughout the opening. Keep
    // navigation live, without sampling photographs behind an opaque header.
    if (header.dataset.smHomeAperture === 'true') {
      setSurface('solid');
      return;
    }
    // Menus already paint an opaque surface. Sampling the page behind them
    // adds work during interactions without changing any visible lettering.
    if (drawer?.hasAttribute('open') || drawer?.hasAttribute('closing') ||
        menu?.querySelector('details.lv1[open]') || filters?.hasAttribute('open')) {
      setSurface('solid');
      return;
    }
    const bounds = header.getBoundingClientRect();
    if (bounds.height <= 0) return;

    if (header.dataset.smHeaderPage === 'index') {
      const {y, maximum, viewportHeight} = position;
      const footerBounds = footer?.isConnected ? footer.getBoundingClientRect() : null;
      const footerVisible = footerBounds && footerBounds.height > 0 && footerBounds.top <= viewportHeight + 16;
      const topThreshold = header.dataset.smHeaderSurface === 'solid' ? 40 : 24;
      const solid = y <= topThreshold || y >= maximum - 24 || footerVisible || scrollDirection === 'up';
      setSurface(solid ? 'solid' : updateContrast());
      return;
    }

    if (header.dataset.smHeaderPage === 'collection') {
      const end = collection?.isConnected ? collection.getBoundingClientRect().bottom : 0;
      setSurface(end > bounds.bottom ? updateContrast() : 'solid');
      return;
    }
    setSurface('solid');
  }

  function queueUpdate() {
    if (!frame) frame = window.requestAnimationFrame(update);
  }

  function refresh() {
    refreshFrame = 0;
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    clearMotionStyles();
    const previousHeader = header;
    header = document.getElementById('mainHeader');
    if (header !== previousHeader) scrollDirection = 'down';
    lastScrollY = scrollPosition().y;
    directionDistance = 0;
    scrollPending = false;
    main = document.getElementById('mainContent');
    group = document.getElementById('headerGroup');
    menu = header?.querySelector('coretex-menu');
    drawer = menu?.querySelector('#menuDrawerBox');
    menuButton = menu?.querySelector('[data-open="#menuDrawerBox"]');
    filterButton = menu?.querySelector('.sm-header-filter');
    navigation = menu?.querySelector('.menuPrimary');
    inkTargets = header ? [...header.querySelectorAll(inkSelector)] : [];
    const previousAnchors = anchors;
    anchors = header ? [...header.querySelectorAll('.header > .logo, .header > .tools')].map(element =>
      previousAnchors.find(anchor => anchor.element === element) || {element}
    ) : [];
    filters = document.getElementById('filtersBox');
    if (header && menu && group) {
      header.dataset.smOriginalMenu ||= header.getAttribute('coretex-menu');
      spacer = document.getElementById('smHeaderSpacer');
      if (!spacer) {
        spacer = document.createElement('div');
        spacer.id = 'smHeaderSpacer';
        spacer.setAttribute('aria-hidden', 'true');
        group.after(spacer);
      }
      measureExpandedHeader();
    }
    footer = document.getElementById('footerGroup');
    collection = main?.querySelector('.sm-collection') || null;
    sizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      sizeObserver ||= new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.target !== header && entry.target !== group) continue;
          // ResizeObserver supplies geometry after layout; use it to update
          // announcement/app height without measuring around animation writes.
          const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
          const height = box?.blockSize ?? entry.target.getBoundingClientRect().height;
          if (entry.target === header) {
            observedHeaderHeight = height;
            const width = box?.inlineSize ?? entry.target.getBoundingClientRect().width;
            if (Math.abs(width - measuredWidth) > 0.5 ||
                (!desktop.matches && Math.abs(height - expandedHeight) > 0.5) ||
                (desktop.matches && progress === 0 && Math.abs(height - expandedHeight) > 0.5) ||
                (desktop.matches && progress === 1 && Math.abs(height - compactHeight) > 0.5)) needsMeasurement = true;
          } else {
            observedGroupHeight = height;
            if (!desktop.matches) mobileGroupHeight = height;
          }
        }
        groupExtraHeight = observedGroupHeight - observedHeaderHeight;
        queueUpdate();
      });
      [header, main, footer, group].filter(Boolean).forEach(element => sizeObserver.observe(element, {box: 'border-box'}));
    }
    stateObserver?.disconnect();
    if ('MutationObserver' in window) {
      stateObserver ||= new MutationObserver(queueUpdate);
      [menu, filters].filter(Boolean).forEach(element => stateObserver.observe(element, {
        attributes: true, subtree: true, attributeFilter: ['open', 'closing', 'closed'],
      }));
      if (header) stateObserver.observe(header, {attributes: true, attributeFilter: ['data-sm-site-motion', 'data-sm-home-aperture']});
    }
    // Restored page positions and responsive remeasurement start fully settled.
    refreshing = true;
    update();
    refreshing = false;
  }

  function queueRefresh() {
    if (!refreshFrame) refreshFrame = window.requestAnimationFrame(refresh);
  }

  function start() {
    refresh();
    if ('MutationObserver' in window) {
      const relevant = '#mainHeader, #mainContent, #footerGroup, #filtersBox, .sm-collection';
      new MutationObserver(records => {
        if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches(relevant) || node.querySelector(relevant))
        ))) queueRefresh();
      }).observe(document.body, {childList: true, subtree: true});
    }
    window.addEventListener('scroll', onScroll, {passive: true});
    // Mobile browser chrome and the keyboard change viewport height repeatedly.
    // Only a width change requires measuring both navigation layouts again.
    window.addEventListener('resize', () => {
      if (window.innerWidth !== measuredViewportWidth) queueRefresh();
      else {
        if (!sizeObserver) needsMeasurement = true;
        queueUpdate();
      }
    }, {passive: true});
    window.addEventListener('pageshow', queueRefresh);
    desktop.addEventListener?.('change', queueRefresh);
    reducedMotion.addEventListener?.('change', queueUpdate);
    document.addEventListener('focusin', queueUpdate);
    document.addEventListener('focusout', queueUpdate);
    // Lazy media can finish loading beneath an already stationary header.
    ['load', 'loadeddata'].forEach(event => document.addEventListener(event, e => {
      if (main?.contains(e.target)) queueUpdate();
    }, true));
    document.addEventListener('shopify:section:load', queueRefresh);
    document.addEventListener('shopify:section:reorder', queueRefresh);
    document.fonts?.ready.then(queueRefresh);
  }

  window.smEditorialHeader = {
    refresh: queueRefresh,
    ownsMetrics: () => Boolean(header?.isConnected && menu && drawer && group &&
      header.getAttribute('location') === 'top' && ['sticky', 'fixed'].includes(header.getAttribute('mode'))),
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
